export const parentWebsite = 'https://thefuturefound.com';

export function normalizePath(path: string): string {
  // Function to remove trailing slash if present
  return path.replace(/\/$/, '');
}

export function formatLabel(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '-');
}

export function formatClassName(text: string): string {
  return text
    .replace(/\//g, '') // Remove slashes
    .replace(/\s+/g, '-') // Replace one or more spaces with a single hyphen
    .toLowerCase(); // Convert to lowercase
}

export function getPageName(path: string): string {
  // Remove leading and trailing slashes if present
  const cleanedPath = path.replace(/^\/|\/$/g, '');

  // Split the path by slashes and return the last segment
  const pathSegments = cleanedPath.split('/');

  // Ensure that the return value is a string
  return pathSegments[pathSegments.length - 1] || ''; // Fallback to an empty string if no segments
}

export function isInCurrentDirectory(currentPath: string, itemHref: string): boolean {
  // Remove leading and trailing slashes
  const cleanedCurrentPath = currentPath.replace(/^\/|\/$/g, '');
  const cleanedItemHref = itemHref.replace(/^\/|\/$/g, '');

  // Split paths into segments
  const currentSegments = cleanedCurrentPath.split('/');
  const itemSegments = cleanedItemHref.split('/');

  // Check if itemHref is a parent or exact match of the current path
  // eslint-disable-next-line security/detect-object-injection
  return itemSegments.every((segment, index) => currentSegments[index] === segment);
}

export function formatNumber(num: number | string | null | undefined): string {
  if (typeof num === 'number') return num.toLocaleString('en-US');
  if (typeof num === 'string') {
    const parsed = parseFloat(num);
    return isNaN(parsed) ? '0' : parsed.toLocaleString('en-US');
  }
  return '0';
}

export const toCamelCase = (input: string): string =>
  input
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
      index === 0 ? word.toLowerCase() : word.toUpperCase(),
    )
    .replace(/\s+/g, '');

export const transformLabelToClassName = (label: string): string =>
  label
    .toLowerCase()
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\s+/g, '-');

export const normalizeStringForComparison = (str: string): string => {
  return str
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim()
    .toLowerCase();
};

interface TruncateProps {
  readonly text: string;
  readonly maxLength: number;
}

export function truncate({ text, maxLength }: TruncateProps): string {
  const truncated = text.substring(0, maxLength).trim();
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  return truncated.length >= text.length
    ? text
    : `${truncated.substring(0, lastSpaceIndex)}${lastSpaceIndex !== -1 ? '...' : ''}`;
}

export function getCurrentYear(): number {
  return new Date().getFullYear();
}

interface UTMparamLinkProps {
  readonly url: string;
  readonly source: string;
  readonly medium: string;
  readonly name: string;
}

export function UTMparamLink({ url, source, medium, name }: UTMparamLinkProps): string {
  const urlObj = new URL(url);
  urlObj.searchParams.set('utm_source', source);
  urlObj.searchParams.set('utm_medium', medium);
  urlObj.searchParams.set('utm_campaign', name);
  return urlObj.toString();
}

export function formatAsClassName(str: string): string {
  return str
    .trim() // trim leading/trailing whitespace
    .replace(/\s+/g, '-') // replace whitespace with dashes
    .replace(/[^\w-]/g, '') // remove any non-word or non-dash characters
    .toLowerCase();
}

export const daysOfTheWeek: readonly unknown[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
];

export const openingHours = {
  end: '18:00',
  start: '09:00',
};

export const formatContactPointHref = (str: string): string => str.replace(/\s+/g, '');

export const manageClassNames = (...classes: readonly (string | undefined | null)[]): string =>
  classes.filter(Boolean).join(' ');

export const formatMonetaryAmount = (amount: string): string => {
  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(Number(amount));
};

export const formatShape = (shape: string | null | undefined): string | undefined => {
  if (!shape) return undefined;
  return shape.toLowerCase().replace(/\s+/g, '-');
};

export const removeParagraphTags = (html: string): string => {
  return html.replace(/<\/?p>/g, '');
};

export const snakeToPascal = (str: string): string =>
  str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');

export const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
